import React, {useCallback, useEffect, useId, useMemo, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import PropTypes from 'prop-types';

import {
  Box,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Text,
  useDisclosure,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {SelectChevronIcon} from '@amblique/king-living/app/components/icons';
import {useCustomizeJourney} from '@amblique/king-living/app/hooks/use-customize-journey';
import {useProductDetailContentful} from '@amblique/king-living/app/context/product-detail-contentful-context';
import {ModeChangeConfirmationDialog} from '@amblique/king-living/app/components/configuration-selector/mode-change-confirmation-dialog';

/**
 * Dedupe configuration products by product id (stable order).
 *
 * @param {Array} products
 * @returns {Array}
 */
function dedupeConfigProducts(products) {
  if (!Array.isArray(products)) {
    return [];
  }

  const seen = new Set();

  return products.filter((p) => {
    const id = p?.productId || p?.id;

    if (id == null || seen.has(id)) {
      return false;
    }

    seen.add(id);

    return true;
  });
}

/**
 * Picker for choosing which configuration set is active vs "Customise your own".
 * Does not change panel mode — only notifies parent via onSelect(productId | null).
 * Desktop: inline dropdown (tabbable buttons, stays inside panel focus trap). Mobile: Modal + list.
 * Confirmation uses AlertDialog (i18n) instead of window.confirm (P1-1).
 *
 * @param {boolean} props.useModalForPicker - When true (mobile drawer), open options in a Modal; when false, use Menu.
 * @param {Array} props.listSetProducts - Config product hits (preset sets).
 * @param {string} props.currentProductId - Master / page product id ("Customise your own" anchor).
 * @param {string} props.effectiveSelectedId - Currently selected product id (set id or currentProductId when null selected).
 * @param {Function} props.onSelect - (productId: string | null) — null selects customize anchor (master).
 */
export function ConfigurationPanelModePicker({
  currentProductId,
  effectiveSelectedId,
  initialFocusRef,
  listSetProducts = [],
  onSelect,
  useModalForPicker = false,
}) {
  const intl = useIntl();
  const styles = useMultiStyleConfig('ConfigurationSelector', {});
  const modalPicker = useDisclosure();
  const menuPicker = useDisclosure();
  const confirmDialog = useDisclosure();
  const desktopPickerContainerRef = useRef(null);
  const desktopOptionsListId = useId();

  const {committedSelection, shouldConfirmModeChange, switchToCustomizeInFlyout} = useCustomizeJourney();
  const contentfulData = useProductDetailContentful();
  const warningMessageSwitchCustomizeMode = contentfulData?.warningMessageSwitchCustomizeMode;

  /** @type {null | {kind: 'set', productId: string} | {kind: 'customize'}} */
  const [confirmIntent, setConfirmIntent] = useState(null);

  const customizeLabel = intl.formatMessage({
    defaultMessage: 'Customise your own',
    id: 'customize_panel.title',
  });

  const modalPickerDialogAriaLabel = intl.formatMessage({
    defaultMessage: 'Choose configuration',
    id: 'configuration_selector.panel_mode.modal_title',
  });

  const openPickerAriaLabel = intl.formatMessage({
    defaultMessage: 'Open configuration menu',
    id: 'configuration_selector.panel_mode.open_menu',
  });

  const uniqueProducts = useMemo(() => dedupeConfigProducts(listSetProducts), [listSetProducts]);

  const anchorId = currentProductId ?? '';

  const isCustomizeAnchorSelected = useMemo(() => {
    if (!anchorId) {
      return effectiveSelectedId == null || effectiveSelectedId === '';
    }

    return String(effectiveSelectedId ?? '') === String(anchorId);
  }, [anchorId, effectiveSelectedId]);

  const triggerLabel = useMemo(() => {
    if (isCustomizeAnchorSelected) {
      return customizeLabel;
    }

    const hit = uniqueProducts.find(
      (p) => String(p.productId || p.id) === String(effectiveSelectedId)
    );

    if (hit) {
      return hit.displayTitle ?? hit.name ?? String(effectiveSelectedId);
    }

    return customizeLabel;
  }, [customizeLabel, effectiveSelectedId, isCustomizeAnchorSelected, uniqueProducts]);

  const closePickerUi = useCallback(() => {
    if (useModalForPicker) {
      modalPicker.onClose();
    } else {
      menuPicker.onClose();
    }
  }, [menuPicker.onClose, modalPicker.onClose, useModalForPicker]);

  const handleCancelConfirm = useCallback(() => {
    setConfirmIntent(null);
    confirmDialog.onClose();
  }, [confirmDialog]);

  const handleConfirmIntent = useCallback(() => {
    if (!confirmIntent) {
      return;
    }

    if (confirmIntent.kind === 'set') {
      onSelect?.(confirmIntent.productId);
    } else {
      switchToCustomizeInFlyout();
      onSelect?.(null);
    }

    setConfirmIntent(null);
    confirmDialog.onClose();
    closePickerUi();
  }, [closePickerUi, confirmDialog, confirmIntent, onSelect, switchToCustomizeInFlyout]);

  const handleActivateSet = useCallback(
    (productId) => {
      const isSameSetAlreadySelected =
        committedSelection.mode === 'set' &&
        String(committedSelection.productId) === String(productId);

      if (isSameSetAlreadySelected) {
        closePickerUi();

        return;
      }

      if (shouldConfirmModeChange('set', productId)) {
        setConfirmIntent({kind: 'set', productId});
        confirmDialog.onOpen();
      } else {
        closePickerUi();
        onSelect?.(productId);
      }
    },
    [closePickerUi, confirmDialog, committedSelection, onSelect, shouldConfirmModeChange]
  );

  const handleActivateCustomize = useCallback(() => {
    const alreadyInCustomizeMode = committedSelection.mode === 'customize';

    if (alreadyInCustomizeMode) {
      closePickerUi();

      return;
    }

    if (shouldConfirmModeChange('customize', null)) {
      setConfirmIntent({kind: 'customize'});
      confirmDialog.onOpen();
    } else {
      closePickerUi();
      onSelect?.(null);
    }
  }, [closePickerUi, confirmDialog, committedSelection, onSelect, shouldConfirmModeChange]);

  useEffect(() => {
    if (useModalForPicker || !menuPicker.isOpen) return undefined;

    const handlePointerDown = (event) => {
      if (desktopPickerContainerRef.current?.contains(event.target)) return;

      menuPicker.onClose();
    };

    document.addEventListener('mousedown', handlePointerDown);

    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [menuPicker.isOpen, menuPicker.onClose, useModalForPicker]);

  useEffect(() => {
    if (useModalForPicker || !menuPicker.isOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;

      event.preventDefault();
      event.stopPropagation();
      menuPicker.onClose();
    };

    document.addEventListener('keydown', handleKeyDown, true);

    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [menuPicker.isOpen, menuPicker.onClose, useModalForPicker]);

  const handleDesktopTriggerClick = useCallback(() => {
    if (menuPicker.isOpen) {
      menuPicker.onClose();
    } else {
      menuPicker.onOpen();
    }
  }, [menuPicker.isOpen, menuPicker.onClose, menuPicker.onOpen]);

  const handleDesktopTriggerKeyDown = useCallback((event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleDesktopTriggerClick();
    } else if (event.key === 'ArrowDown' && !menuPicker.isOpen) {
      event.preventDefault();
      menuPicker.onOpen();
    } else if (event.key === 'Escape' && menuPicker.isOpen) {
      event.preventDefault();
      event.stopPropagation();
      menuPicker.onClose();
    }
  }, [handleDesktopTriggerClick, menuPicker.isOpen, menuPicker.onClose, menuPicker.onOpen]);

  const renderOptionLabel = (label, isSelected) => (
    <Flex
      alignItems="center"
      alignSelf="stretch"
      className="option-item"
      flex="1 0 0"
      gap={4}
      justifyContent="space-between"
      width="100%"
    >
      <Text
        {...(isSelected ? styles.panelModePickerOptionLabelSelected : styles.panelModePickerOptionLabel)}
        noOfLines={2}
        textAlign="left"
      >
        {label}
      </Text>
      {isSelected ? (
        <Box flexShrink={0} h="20px" w="20px" aria-hidden>
          <svg fill="none" height="20" viewBox="0 0 20 20" width="20" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M18.7343 1.75926C18.9855 1.45524 19.4361 1.41244 19.7401 1.66356C20.0442 1.91485 20.0871 2.36532 19.8358 2.66941L7.39256 17.7259L7.39158 17.7288C7.18787 17.9733 6.93151 18.1697 6.64257 18.303C6.42596 18.4029 6.19425 18.4659 5.958 18.4895L5.71972 18.5003C5.39635 18.4962 5.07737 18.4183 4.78808 18.2737C4.49956 18.1294 4.24691 17.9216 4.04981 17.6663L0.150417 12.6527C-0.0916449 12.3415 -0.0355344 11.893 0.275417 11.6507C0.547889 11.4388 0.925352 11.4552 1.17775 11.6713L1.27736 11.7757L5.17773 16.7903L5.17968 16.7933C5.24542 16.8786 5.3304 16.9482 5.42675 16.9964C5.52283 17.0444 5.62894 17.0691 5.73632 17.0706C5.84231 17.0718 5.94767 17.0495 6.04394 17.0052C6.14012 16.9608 6.22609 16.8951 6.29393 16.8138L18.7343 1.75926Z"
              fill="#8C683B"
            />
          </svg>
        </Box>
      ) : null}
    </Flex>
  );

  const renderDesktopOptionButton = (key, {isFirst, isLast, isSelected, label, onActivate}) => (
    <Box
      key={key}
      as="button"
      type="button"
      {...styles.panelModePickerMenuItem}
      {...(isFirst ? styles.panelModePickerMenuItemFirst : {})}
      {...(isLast ? styles.panelModePickerMenuItemLast : {})}
      {...(isSelected ? styles.panelModePickerMenuItemSelected : {})}
      onClick={onActivate}
    >
      {renderOptionLabel(label, isSelected)}
    </Box>
  );

  const alertDialog = (
    <ModeChangeConfirmationDialog
      contentfulContent={warningMessageSwitchCustomizeMode}
      isOpen={confirmDialog.isOpen}
      onCancel={handleCancelConfirm}
      onConfirm={handleConfirmIntent}
    />
  );

  if (useModalForPicker) {
    return (
      <>
        <Box className="panel-choose-set-picker panel-choose-set-picker__modal-container" {...styles.panelModePickerRoot}>
          <Flex
            ref={initialFocusRef}
            alignItems="center"
            aria-expanded={modalPicker.isOpen}
            aria-haspopup="dialog"
            aria-label={openPickerAriaLabel}
            as="button"
            display="flex"
            justifyContent="flex-start"
            minWidth={0}
            type="button"
            width="100%"
            {...styles.panelModePickerTrigger}
            onClick={modalPicker.onOpen}
          >
            <Flex alignItems="center" display="flex" gap={6} minWidth={0} width="100%">
              <Text
                flexShrink={1}
                maxW="min(315px, calc(100% - 44px))"
                minW={0}
                noOfLines={1}
                textAlign="left"
                fontWeight={600}
                className="panel-mode-picker-option-label-1"
              >
                {triggerLabel}
              </Text>
              <SelectChevronIcon flexShrink={0} aria-hidden {...styles.panelModePickerChevron} />
            </Flex>
          </Flex>

          <Modal
            aria-label={modalPickerDialogAriaLabel}
            isOpen={modalPicker.isOpen}
            scrollBehavior="inside"
            isCentered
            onClose={modalPicker.onClose}
          >
            <ModalOverlay {...styles.panelModePickerModalOverlay} />
            <ModalContent
              {...styles.panelModePickerModalContent}
              backgroundColor="transparent"
              boxShadow="none"
            >
              <ModalBody {...styles.panelModePickerModalBody}>
                <Box className="panel-mode-picker-options-surface" {...styles.panelModePickerOptionsSurface}>
                  <Box className="panel-mode-picker-list" {...styles.panelModePickerList}>
                    {uniqueProducts.map((hit, index) => {
                      const id = hit.productId || hit.id;
                      const label = hit.displayTitle ?? hit.name ?? id;
                      const isSelected = String(effectiveSelectedId) === String(id);

                      return (
                        <Box
                          key={id}
                          as="button"
                          type="button"
                          {...styles.panelModePickerListButton}
                          {...(index === 0 ? styles.panelModePickerListButtonFirst : {})}
                          {...(isSelected ? styles.panelModePickerListButtonSelected : {})}
                          onClick={() => handleActivateSet(id)}
                        >
                          {renderOptionLabel(label, isSelected)}
                        </Box>
                      );
                    })}
                    <Box
                      as="button"
                      type="button"
                      {...styles.panelModePickerListButton}
                      {...(uniqueProducts.length === 0 ? styles.panelModePickerListButtonFirst : {})}
                      {...styles.panelModePickerListButtonLast}
                      {...(isCustomizeAnchorSelected ? styles.panelModePickerListButtonSelected : {})}
                      onClick={handleActivateCustomize}
                    >
                      {renderOptionLabel(customizeLabel, isCustomizeAnchorSelected)}
                    </Box>
                  </Box>
                </Box>
              </ModalBody>
            </ModalContent>
          </Modal>
        </Box>
        {alertDialog}
      </>
    );
  }

  return (
    <>
      <Box
        ref={desktopPickerContainerRef}
        className="panel-choose-set-picker panel-choose-set-picker__menu-container"
        position="relative"
        {...styles.panelModePickerRoot}
      >
        <Box
          ref={initialFocusRef}
          aria-controls={menuPicker.isOpen ? desktopOptionsListId : undefined}
          aria-expanded={menuPicker.isOpen}
          aria-haspopup="true"
          aria-label={openPickerAriaLabel}
          as="button"
          className="panel-mode-picker-trigger"
          display="flex"
          justifyContent="flex-start"
          minWidth={0}
          type="button"
          width="100%"
          {...styles.panelModePickerTrigger}
          onClick={handleDesktopTriggerClick}
          onKeyDown={handleDesktopTriggerKeyDown}
        >
          <Flex alignItems="center" as="span" display="inline-flex" gap={6} minWidth={0} width="100%">
            <Text
              className="panel-mode-picker-option-label"
              flexShrink={1}
              maxW="min(315px, calc(100% - 44px))"
              minW={0}
              noOfLines={1}
              fontWeight={600}
              textAlign="left"
            >
              {triggerLabel}
            </Text>
            <SelectChevronIcon flexShrink={0} aria-hidden {...styles.panelModePickerChevron} />
          </Flex>
        </Box>
        {menuPicker.isOpen ? (
          <Box
            id={desktopOptionsListId}
            className="panel-mode-picker-menu-list"
            left={0}
            position="absolute"
            top="calc(100% + 4px)"
            width="100%"
            zIndex={2000}
            {...styles.panelModePickerMenuList}
          >
            {uniqueProducts.map((hit, index) => {
              const id = hit.productId || hit.id;
              const label = hit.displayTitle ?? hit.name ?? id;
              const isSelected = String(effectiveSelectedId) === String(id);

              return renderDesktopOptionButton(id, {
                isFirst: index === 0,
                isLast: false,
                isSelected,
                label,
                onActivate: () => handleActivateSet(id),
              });
            })}
            {renderDesktopOptionButton('customize', {
              isFirst: uniqueProducts.length === 0,
              isLast: true,
              isSelected: isCustomizeAnchorSelected,
              label: customizeLabel,
              onActivate: handleActivateCustomize,
            })}
          </Box>
        ) : null}
      </Box>
      {alertDialog}
    </>
  );
}

ConfigurationPanelModePicker.displayName = 'ConfigurationPanelModePicker';

ConfigurationPanelModePicker.propTypes = {
  currentProductId: PropTypes.string,
  effectiveSelectedId: PropTypes.string,
  initialFocusRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({current: PropTypes.any}),
  ]),
  listSetProducts: PropTypes.array,
  onSelect: PropTypes.func,
  useModalForPicker: PropTypes.bool,
};
